import { Alert } from '@nbc-design/alert';
import { Button } from '@nbc-design/button';
import { Card } from '@nbc-design/card';
import { FormGroup } from '@nbc-design/form-group';
import { Heading } from '@nbc-design/heading';
import { Security } from '@nbc-design/icons/lib/web/Security';
import { Input } from '@nbc-design/input';
import { Password } from '@nbc-design/password';
import { Text } from '@nbc-design/text';
import TextList from '@nbc-design/text-list';
import BaoTooltip from 'components/BaoTooltip/BaoTooltip';
import ErrorMessageDisplay from 'components/errorManager/ErrorMessageDisplay';
import configs from 'configs';
import React, { ChangeEvent, ClipboardEvent, Component, FocusEvent, ReactNode } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import { reactLocalStorage } from 'reactjs-localstorage';
import { sendOutOfFormFlowAnalyticsEvent, sendOutOfFormFlowErrorAnalyticsEvent } from 'services/analytics';
import { getTextFactory } from 'utils/TextUtils';
import PasswordCriteria, { PasswordCriterionProps } from '../../NewAccountRequest/components/PasswordCriteria';
import { CreateProfileStep } from '../constant';
import { cmsEncryptValue, getCertificate, sendStepLoadedAnalyticsEvent } from '../utils';
import { getFieldsValidation } from '../validation';
import { CredentialsCreationProps, CredentialsCreationState } from './CredentialsCreation.type';

class CredentialsCreation extends Component<CredentialsCreationProps, CredentialsCreationState> {
  state: CredentialsCreationState = {
    formValues: {
      email: reactLocalStorage.get('userEmail') || '',
      password: '',
    },
    touchedFields: {
      email: false,
      password: false,
    },
    formErrors: { email: '', password: '', connection: '' },
    isSubmitted: false,
    isLoading: false,
    isFormValid: false,
    isValidPassword: false,
  };

  getText = getTextFactory(this.props.intl, 'createCredentials');
  getTextError = getTextFactory(this.props.intl, 'error');
  getGlobalText = getTextFactory(this.props.intl, 'global');
  getAccessibilityText = getTextFactory(this.props.intl, 'accessibility');

  config = {
    ID: 'CREDENTIALS_CREATION',
  };

  componentDidMount = () => {
    sendStepLoadedAnalyticsEvent('PROFILE_CREDENTIALS');
  };

  validationConditions = (): PasswordCriterionProps[] => {
    const {
      formValues: { password },
    } = this.state;
    return [
      { id: 'oneLetter', label: this.getText('passwordRequirement2'), isValid: /[A-Z]+/.test(password) },
      { id: 'oneNumber', label: this.getText('passwordRequirement1'), isValid: /\d+/.test(password) },
      { id: 'noSpace', label: this.getText('passwordRequirement3'), isValid: /^\S+$/.test(password) },
      {
        id: 'length',
        label: this.getText('passwordRequirement4'),
        isValid: password.length >= 12 && password.length <= 25,
      },
    ];
  };

  validatePassword = (): void => {
    const isValidPassword = this.validationConditions().reduce(
      (validPassword, { isValid }) => validPassword && isValid,
      true,
    );
    this.setState({ isValidPassword });
  };

  validateForm() {
    this.formErrorsValues() && sendOutOfFormFlowErrorAnalyticsEvent(this.state.formErrors, 'profile creation');

    this.setState({ isFormValid: this.isFormValid() });
  }

  setError = (name: string, error: string): void => {
    this.setState(({ formErrors }) => ({
      formErrors: {
        ...formErrors,
        [name]: error ? this.getTextError(error) : '',
      },
    }));
  };

  isFormValid = (): boolean => {
    return this.hasRequiredValues() && this.state.isValidPassword && !this.formErrorsValues();
  };

  hasRequiredValues = (): boolean => {
    return Object.values(this.state.formValues).every((value) => value);
  };

  formErrorsValues = (): string | boolean | undefined => {
    return Object.values(this.state.formErrors).find((value) => value !== '');
  };

  validateField = (fieldName: string, value: string): void => {
    const { formErrors } = getFieldsValidation({
      fieldName,
      value,
    });

    this.setError(fieldName, formErrors[fieldName]);
  };

  handleError = (response): void => {
    console.log('ERROR SERVER RESPONSE: ', response);

    this.setError('connection', 'connection');
    this.setState({ isLoading: false });

    sendOutOfFormFlowAnalyticsEvent('' + this.formErrorsValues(), '' + this.config.ID);
  };

  saveUserCredentials = async (): Promise<void> => {
    const {
      formValues: { email, password },
    } = this.state;
    const { setUserCredentials, setCardWrapperState } = this.props;

    try {
      const isIamxEncryptionEnabled = configs.params.FEATURE_TOGGLE.IAMX_ENCRYPTION;
      let encryptedPassword: string | undefined = password;
      if (isIamxEncryptionEnabled) {
        const certificate = await getCertificate();
        encryptedPassword = await cmsEncryptValue(certificate, password);
      }

      await setUserCredentials({ email, password, encryptedPassword });

      this.setState({ isLoading: false, isSubmitted: true }, () => {
        setCardWrapperState({ step: CreateProfileStep.PERSONAL_INFO_STEP });
      });
    } catch (err) {
      this.handleError(err);
    }
  };

  validateAllFields = (email: string, password: string): void => {
    this.validateField('email', email);
    this.validateField('password', password);
    this.validateForm();
  };

  handleFormSubmit = (): void => {
    const {
      formValues: { email, password },
    } = this.state;

    this.validateAllFields(email, password);

    const isFormValid = this.isFormValid();

    this.setState({
      isSubmitted: true,
      isLoading: isFormValid,
    });

    isFormValid && this.saveUserCredentials().catch(this.handleError);
  };

  onChange = (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {
      target: { name, value },
    } = event;

    const formValues = this.state.formValues;
    formValues[name] = value.trim();

    this.setState({ formValues }, () => {
      this.state.touchedFields[name] && this.validateField(name, value.trim());
      this.validatePassword();
    });
  };

  onBlur = (event: FocusEvent<HTMLInputElement | HTMLSelectElement>): void => {
    const {
      target: { name, value },
    } = event;

    const touchedFields = this.state.touchedFields;
    touchedFields[name] = true;

    this.setState({ touchedFields }, () => {
      this.validateField(name, value.trim());
    });
  };

  onPaste = (event: ClipboardEvent<HTMLInputElement>): void => {
    event.preventDefault();
  };

  render(): ReactNode {
    const { isError } = this.props;

    const {
      formValues: { password },
      formErrors,
      isLoading,
    } = this.state;

    const validations = this.validationConditions();

    let notValidConditions = validations
      .filter((condition) => !condition.isValid)
      .map((condition) => `${condition.id}-condition`)
      .join(' ');

    if (!notValidConditions.length) {
      notValidConditions = 'requirements-met';
    }

    return (
      <>
        {isError && (
          <Alert
            appearance="error"
            title={this.getText('noCreationErrorTitle')}
            description={this.getText('noCreationErrorText')}
            ariaLabelIcon={this.getAccessibilityText('icon.error')}
          />
        )}

        <Card border="shadow" className="spc-creationProfil__card mb-4 mt-4">
          <form
            onSubmit={(event) => {
              event.preventDefault();
              this.handleFormSubmit();
            }}
          >
            <div className="spc-creationProfil__securityHeader">
              <Security title="security" size={{ width: 40, height: 40 }} className="mr-4 text-navy-900" />
              <TextList size="small">
                <Text size={'small'} className="mb-1 profil-explanation">
                  {this.getText('explanation1')}
                </Text>
                <TextList.Item className="mb-1">{this.getText('explanation2')}</TextList.Item>
                <TextList.Item className="mb-0">{this.getText('explanation3')}</TextList.Item>
              </TextList>
            </div>

            <div className="spc-creationProfil__cardBody spc-creationProfil__cardBody--fullContentMobile">
              <div className="spc-creationProfil__stepNumber">1</div>
              <div className="spc-creationProfil__stepTitle">
                <Heading type="h2" size={5}>
                  {this.getText('stepTitle1')}
                </Heading>
                <BaoTooltip
                  name="credentialsCreation"
                  stepId={'CREATE_USER_PROFILE'}
                  content={this.getText('stepTitleTooltip')}
                  position="top"
                />
              </div>
              <div></div>
              <div className="pt-3">
                <Row>
                  <Col xs={12} md={12} lg={12}>
                    <FormGroup
                      label={{
                        text: this.getText('identificationEmailLabel'),
                        htmlFor: 'identification-email-input',
                      }}
                      validate={{ hasError: !!formErrors.email, errorMsg: formErrors.email }}
                      data-test="label_identificationEmail"
                      length="large"
                    >
                      <Input
                        id="identification-email-input"
                        name="email"
                        type="email"
                        maxLength={64}
                        onChange={(e) => {
                          this.onChange(e);
                          reactLocalStorage.set('userEmail', e.target.value);
                        }}
                        onBlur={this.onBlur}
                        onPaste={this.onPaste}
                        data-test="field_identificationEmail"
                        placeholder={this.getText('identificationEmailPlaceholder')}
                        defaultValue={reactLocalStorage.get('userEmail')}
                        disabled={isLoading}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={12} lg={12}>
                    <FormGroup
                      label={{ text: this.getText('passwordLabel'), htmlFor: 'password-input' }}
                      validate={{ hasError: !!formErrors.password, errorMsg: formErrors.password }}
                      data-test="label_password"
                      introduction={{
                        text: this.getText('passwordSubLabel'),
                        id: 'mdpDesc',
                      }}
                      length="large"
                    >
                      <Password
                        id="password-input"
                        name="password"
                        maxLength={25}
                        value={password}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        onPaste={this.onPaste}
                        data-test="field_password"
                        autoComplete="off"
                        ariaLabelShow={this.getText('showPassword')}
                        ariaLabelHide={this.getText('hidePassword')}
                        aria-describedby={notValidConditions}
                        disabled={isLoading}
                      />
                    </FormGroup>

                    <PasswordCriteria
                      criteria={this.validationConditions()}
                      requirementMetLabel={this.getText('requirementsMet')}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12} md={12} lg={12} className="consent-explanation">
                    <Text appearance="muted" size="xSmall">
                      {this.getText('consentExplanation1')}
                    </Text>
                    <Text appearance="muted" size="xSmall">
                      {this.getText('consentExplanation2')}
                    </Text>
                  </Col>
                </Row>
                <ErrorMessageDisplay visible={!!formErrors.connection} message={formErrors.connection} />
              </div>
            </div>
            <div className="spc-creationProfil__cardFooter">
              <Button
                appearance="primary"
                type="submit"
                loading={{ isLoading: isLoading, ariaLabel: this.getGlobalText('loading') }}
              >
                {this.getGlobalText('button.continue')}
              </Button>
            </div>
          </form>
        </Card>
      </>
    );
  }
}

export default CredentialsCreation;
